import { shortenAddress } from '../lib/btcAddress';
import { formatBtc, formatUsd, formatSignedSats } from '../lib/format';
import type { WalletSummary } from '../lib/types';
import { CopyButton } from './CopyButton';
import { DepthContours } from './DepthContours';
import { SourceStatusBadge } from './SourceStatusBadge';
import { Skeleton } from './Skeleton';
import styles from './WalletSummaryCard.module.css';

export interface WalletSummaryCardProps {
  summary: WalletSummary | undefined;
  previousSummary: WalletSummary | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  staleSummary?: WalletSummary | null;
}

export function WalletSummaryCard({
  summary,
  previousSummary,
  isLoading,
  isError,
  errorMessage,
  staleSummary,
}: WalletSummaryCardProps) {
  if (isLoading && !summary) {
    // Same shape as the loaded hero, so nothing jumps when data arrives.
    return (
      <section className={styles.hero} aria-label="Wallet summary loading">
        <div className={styles.heroFigure}>
          <Skeleton width="9rem" height="1rem" />
          <Skeleton width="16rem" height="3rem" />
          <Skeleton width="8rem" height="1.25rem" />
        </div>
        <Skeleton width="100%" height="9rem" />
      </section>
    );
  }

  if (isError) {
    const fallback = staleSummary;
    return (
      <section className={styles.hero} aria-label="Wallet summary error">
        <div className={styles.errorBanner} role="alert">
          <strong>Wallet data unavailable.</strong>
          <span>{errorMessage ?? 'The Blockstream source could not be reached.'}</span>
        </div>
        {fallback && (
          <>
            <p className={styles.staleNote}>
              Showing cached data from {fallback.generatedAt}.
            </p>
            <SummaryBody summary={fallback} previousSummary={previousSummary} />
          </>
        )}
      </section>
    );
  }

  if (!summary) return null;

  return (
    <section className={styles.hero} aria-labelledby="wallet-summary-heading">
      <DepthContours />
      <SummaryBody summary={summary} previousSummary={previousSummary} />
    </section>
  );
}

/** Headline sizes step down with length so large balances never wrap. */
function heroSizeClass(text: string): string {
  if (text.length <= 11) return styles.heroValue;
  if (text.length <= 14) return `${styles.heroValue} ${styles.heroValueMd}`;
  return `${styles.heroValue} ${styles.heroValueSm}`;
}

function SummaryBody({
  summary,
  previousSummary,
}: {
  summary: WalletSummary;
  previousSummary: WalletSummary | null;
}) {
  const delta =
    previousSummary && previousSummary.address === summary.address
      ? summary.totalBalanceSat - previousSummary.totalBalanceSat
      : null;
  const usdUnavailable = summary.btcUsdPrice === null;
  // A failed CoinGecko lookup isn't worth a red badge when the Coinbase
  // fallback priced the wallet anyway — show the source that actually did.
  const visibleSources = summary.sources.filter(
    (s) =>
      !(
        s.source === 'coingecko' &&
        !s.ok &&
        summary.sources.some((o) => o.source === 'coinbase' && o.ok)
      ),
  );
  const usdText = formatUsd(summary.totalValueUsd);
  const btcText = formatBtc(summary.totalBalanceSat);
  const confirmedText = `${formatBtc(summary.confirmedBalanceSat)} BTC`;
  const mempoolText = formatSignedSats(summary.mempoolDeltaSat);
  // Two figures side by side only fit while they're short; a large wallet's
  // "75,305.67767311 BTC" needs the full width or it runs into its neighbour.
  const longFigures = Math.max(confirmedText.length, mempoolText.length) > 15;

  return (
    <>
      <div className={styles.headerRow}>
        <h2 id="wallet-summary-heading" className={styles.label}>
          {summary.label}
        </h2>
        <div className={styles.addressRow}>
          <span aria-label={`Full address ${summary.address}`}>
            {shortenAddress(summary.address)}
          </span>
          <CopyButton value={summary.address} label="Copy address" />
        </div>
      </div>

      {summary.totalValueUsd !== null ? (
        <div className={styles.heroFigure}>
          <span className={styles.metricLabel}>Total value</span>
          <span className={heroSizeClass(usdText)}>{usdText}</span>
          <span className={styles.heroSecondary}>{btcText} BTC</span>
        </div>
      ) : (
        <div className={styles.heroFigure}>
          <span className={styles.metricLabel}>Total balance</span>
          <span className={heroSizeClass(`${btcText} BTC`)}>
            {btcText}
            <span className={styles.heroUnit}> BTC</span>
          </span>
        </div>
      )}

      {usdUnavailable && (
        <p className={styles.warning} role="status">
          USD pricing is temporarily unavailable. BTC balance remains accurate.
        </p>
      )}

      <div className={styles.details}>
        <div
          className={
            longFigures
              ? `${styles.subMetricRow} ${styles.subMetricRowStacked}`
              : styles.subMetricRow
          }
        >
          <div className={styles.subMetric}>
            <span className={styles.metricLabel}>Confirmed</span>
            <span>{confirmedText}</span>
          </div>
          <div className={styles.subMetric}>
            <span className={styles.metricLabel}>Mempool adjustment</span>
            <span>{mempoolText}</span>
          </div>
          <div className={styles.subMetric}>
            <span className={styles.metricLabel}>BTC/USD price</span>
            <span>{formatUsd(summary.btcUsdPrice)}</span>
          </div>
          <div className={styles.subMetric}>
            <span className={styles.metricLabel}>Transactions</span>
            <span>{summary.txCount}</span>
          </div>
        </div>
        <div className={styles.sources}>
          {visibleSources.map((source) => (
            <SourceStatusBadge key={source.source} source={source} />
          ))}
        </div>
      </div>

      {delta !== null && (
        <p className={styles.deltaNote} aria-live="polite">
          {delta === 0 ? 'No change' : formatSignedSats(delta)} since your last successful
          visit.
        </p>
      )}
    </>
  );
}
